import React from 'react';
import { Link } from 'react-router';
import { Points } from 'components/Points/Points';
import { SetIcon } from 'components/SetIcon';
import { AssetUrls } from 'config/assets';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { CardFinish, FINISH_ORDER } from 'utils/finishes';
import { FinishTag } from 'components/FinishTag';
import './CardInfoCard.css';

/**
 * The two trade signals, mirroring the two sidebar filters exactly: offered is
 * the receive side, wanted is the send side. Both are counts of PEOPLE, not
 * copies, and both are printing-level.
 *
 * There is deliberately no raw "holding" total — it read as supply while
 * counting people who may not be offering anything, which is the ambiguity this
 * vocabulary exists to remove. `ownedCount` ("In your collection") is absent
 * too: it is per-viewer, and search-light is a public cacheable route.
 */
export interface CardSignal {
  /** Eligible users offering this printing for trade. */
  offeredCount?: number;
  /** Eligible users who want this printing. */
  wantedCount?: number;
}

interface CardInfoCardProps {
  title: string;
  altName?: string;
  cardId: string | number;
  setName?: string;
  setCode?: string;
  setIconUrl?: string;
  collectorNumber?: string;
  typeLine?: string;
  nonFoilPrice?: number;
  foilPrice?: number;
  etchedPrice?: number;
  /**
   * Finishes the printing exists in (from getAvailableFinishes), present only
   * when the payload declares them. Drives the per-finish price rows.
   */
  finishes?: CardFinish[];
  signal?: CardSignal;
  /** Drops the type line and signal rows. Owner derives this from measured width. */
  compact?: boolean;
  /** Opens the card action menu — the same thing clicking the card does. */
  onOpenActions?: () => void;
}

/** A count only renders when the backend actually sent a number. */
const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * The hover face: information only, no buttons.
 *
 * Actions live in CardActionMenu, opened by clicking the card. Splitting the
 * two is the point — six actions do not fit on a card face, and stacking them
 * turns every hover into a menu the user has to read before they can act.
 */
export const CardInfoCard: React.FC<CardInfoCardProps> = ({
  title,
  altName,
  cardId,
  setName,
  setCode,
  setIconUrl,
  collectorNumber,
  typeLine,
  nonFoilPrice,
  foilPrice,
  etchedPrice,
  finishes,
  signal,
  compact = false,
  onOpenActions,
}) => {
  // Same target the old overlay produced. Rendered as a real <Link> so the
  // browser handles Ctrl/Cmd-click, middle-click and right-click natively —
  // programmatic navigation cannot honour modifier keys.
  const cardDetailUrl = buildCardUrl({
    name: title,
    setCode,
    collectorNumber,
    scryfallID: String(cardId),
  });

  // Prefer the non-foil price, fall back to foil, otherwise the card is
  // unpriced — which renders "???" and never 0.
  const price =
    nonFoilPrice !== undefined && nonFoilPrice !== null && nonFoilPrice !== 0
      ? nonFoilPrice
      : foilPrice !== undefined && foilPrice !== null && foilPrice !== 0
        ? foilPrice
        : null;

  // Per-finish pricing (C-1): one row per declared finish, canonical order,
  // each read from its own column; an unpriced finish shows "???", never 0.
  const priceByFinish: Record<CardFinish, number | undefined> = {
    nonfoil: nonFoilPrice,
    foil: foilPrice,
    etched: etchedPrice,
  };
  const finishRows =
    !compact && finishes && finishes.length > 0
      ? FINISH_ORDER.filter((f) => finishes.includes(f)).map((f) => ({
          finish: f,
          points: priceByFinish[f] != null && priceByFinish[f] !== 0 ? priceByFinish[f] : null,
        }))
      : [];

  const rows = compact
    ? []
    : [
        isCount(signal?.offeredCount) && {
          key: 'offered',
          label: 'Offered by',
          value: signal!.offeredCount!.toLocaleString(),
        },
        // Accented: demand is the send side, and sending is the primary activity.
        isCount(signal?.wantedCount) && {
          key: 'wanted',
          label: 'Wanted by',
          value: signal!.wantedCount!.toLocaleString(),
          accent: true,
        },
      ].filter(Boolean as unknown as (v: unknown) => v is {
        key: string;
        label: string;
        value: string;
        accent?: boolean;
      });

  return (
    <div className={`card-info-card ${compact ? 'compact' : ''}`}>
      {/* stopPropagation keeps the parent card's click handler from opening the
          action menu; we deliberately do NOT preventDefault so the href drives
          navigation and modifier-clicks still work. */}
      <Link
        className="card-info-title-row"
        to={cardDetailUrl}
        state={{
          from: 'search',
          returnPath: window.location.pathname + window.location.search,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="card-info-title">{title}</span>
        <img
          className="card-info-arrow"
          src={AssetUrls.search.arrowDiagonal}
          alt="View details"
        />
      </Link>

      {altName && <span className="card-info-subtitle">{altName}</span>}

      {setName && (
        <div className="card-info-set">
          <SetIcon setIconUrl={setIconUrl} setName={setName} size="sm" />
          <span className="card-info-set-name">{setName}</span>
          {collectorNumber && (
            <span className="card-info-collector">#{collectorNumber}</span>
          )}
        </div>
      )}

      {!compact && typeLine && <span className="card-info-type">{typeLine}</span>}

      {finishRows.length > 0 && (
        <div className="card-info-finishes" data-testid="card-info-finishes">
          {finishRows.map((row) => (
            <div className="card-info-finish-row" key={row.finish} data-finish={row.finish}>
              <FinishTag finish={row.finish} size="cell" className="card-info-finish-label" />
              {row.points !== null ? (
                <Points type="small" points={row.points} />
              ) : (
                <span className="card-info-finish-unpriced">???</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="card-info-price">
        {price !== null ? (
          <Points type="big" points={price} />
        ) : (
          <div className="card-info-unpriced">???</div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="card-info-signal">
          {rows.map((row) => (
            <div className="card-info-signal-row" key={row.key}>
              <span className="card-info-signal-label">{row.label}</span>
              <span
                className={`card-info-signal-value ${row.accent ? 'accent' : ''}`}
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* The way into the action menu, so it is a real button: focusable, and
          Enter/Space reach it without going through the parent card. It opens
          exactly what clicking the card opens. stopPropagation prevents the
          parent's handler from firing second and immediately toggling it shut. */}
      <button
        type="button"
        className="card-info-hint"
        onClick={(e) => {
          e.stopPropagation();
          onOpenActions?.();
        }}
      >
        Click for actions
        {/* Masked, not an <img> with a brightness/invert filter: the mask is
            painted with currentColor, so the glyph can never drift from the
            label's colour across the default, hover and focus states. */}
        <span
          className="card-info-hint-arrow"
          aria-hidden="true"
          // Sourced from AssetUrls rather than hardcoded in the stylesheet.
          // The *Mask variant is the inlined form: a remote mask-image silently
          // paints nothing if its fetch is refused. See config/assets.ts.
          style={
            {
              '--card-info-hint-arrow-src': AssetUrls.search.arrowSentMask,
            } as React.CSSProperties
          }
        />
      </button>
    </div>
  );
};

export default CardInfoCard;
